import React, { useCallback, useEffect, useState, useRef } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { ObjValidator, ProjectTree, TranslateWrapper } from "components";
import { getHomepage, getProjectId } from "utils";
import { ProductListWithControls } from "components/ProductListOfProject/ProductListWithControls";
import { ProjectHeader } from "components/ProjectHeader/ProjectHeader";
import { Clipboard } from "components/ProjectTree/Clipboard";
import { ToggleShowPictures } from "components/ProjectTree/ToggleShowPictures";
import { ExpandCollapseLevels } from "components/ProjectTree/ExpandCollapseLevels";
import { useTranslate } from "hooks";
import { useProjectDataStore } from "stores/projectStore";
import { PROJECT_CONFIGURATOR_TAB_TYPES } from "variables";

const ProjectConfigurator = Scrivito.connect(({ page }) => {
  const translate = useTranslate();

  const projectId = useProjectDataStore((state) => state.projectId);
  const [activeTab, setActiveTab] = useState(
    PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
  );
  const productListRef = useRef({});

  const handleChangeActiveTab = useCallback((e) => {
    setActiveTab(e.target.id);
  }, []);

  useRedirectEffect();

  const projectSummarySidebarMyStructure = translate(
    "PROJECT_SUMMARY_SIDEBAR_MY_STRUCTURE",
  );
  const projectProductListTitle = translate("PROJECT_PRODUCT_LIST");

  return (
    <>
      <div className="pt-4 h-full d-flex flex-column overflow-auto">
        <div className="ms-3 px-md-5 px-lg-9">
          <div className="position-relative">
            <div className="d-flex">
              <ProjectHeader
                projectId={projectId}
                page={page}
                title={translate("PROJECT_SUMMARY")}
                productListRef={productListRef}
              />
            </div>
            <Nav
              tabs
              className="d-inline-flex border border-info p-1 rounded-3 me-auto mt-4"
            >
              <NavItem>
                <NavLink
                  id={PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE}
                  onClick={handleChangeActiveTab}
                  className={cx("rounded-1 cursor-pointer py-1 fs-18 lh-22", {
                    "bg-blue-300 text-white":
                      activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
                    "text-blue-300":
                      activeTab !== PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
                  })}
                >
                  {translate(
                    "PROJECT_SUMMARY_SIDEBAR_MY_STRUCTURE",
                    null,
                    true,
                  )}
                </NavLink>
              </NavItem>
              {typeof projectSummarySidebarMyStructure === "object" && (
                <TranslateWrapper
                  tooltipText={
                    projectSummarySidebarMyStructure.props.tooltipText
                  }
                  tooltipId={PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE}
                />
              )}
              <NavItem>
                <NavLink
                  id={PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST}
                  onClick={handleChangeActiveTab}
                  className={cx("rounded-1 cursor-pointer py-1 fs-18 lh-22", {
                    "bg-blue-300 text-white":
                      activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST,
                    "text-blue-300":
                      activeTab !== PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST,
                  })}
                >
                  {translate.stringOnly("PROJECT_PRODUCT_LIST", null)}
                </NavLink>
              </NavItem>
              {typeof projectProductListTitle === "object" && (
                <TranslateWrapper
                  tooltipText={projectProductListTitle.props.tooltipText}
                  tooltipId={PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST}
                />
              )}
            </Nav>
            {activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE && (
              <div className="d-flex flex-row align-items-center justify-content-between pt-3">
                <ExpandCollapseLevels />
                <ToggleShowPictures />
              </div>
            )}

            <Clipboard />
          </div>
        </div>
        <TabContent
          activeTab={activeTab}
          className="d-flex flex-column justify-content-start h-100 ms-3"
        >
          <TabPane
            tabId={PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE}
            className={cx("d-flex", {
              "h-100 mt-2 w-100 ps-md-5 ps-lg-9":
                activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE,
            })}
          >
            {activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PROJECT_TREE && (
              <ProjectTree
                projectId={projectId}
                options={{ isFullpage: true }}
              />
            )}
          </TabPane>
          <TabPane
            tabId={PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST}
            className="px-md-5 px-lg-9"
          >
            {activeTab === PROJECT_CONFIGURATOR_TAB_TYPES.PRODUCT_LIST && (
              <ProductListWithControls
                /* TODO: forthcoming feature (hidden acc US-19953) set "hasFilter={false}"  */
                hasFilter={false}
                hasSizeSwitcher
                projectId={projectId}
                productListRef={productListRef}
              />
            )}
          </TabPane>
        </TabContent>
        <Scrivito.ContentTag content={page} attribute="body" tag="div" />
      </div>
    </>
  );
});

const CheckedPage = (props) => (
  <ObjValidator {...props}>
    <ProjectConfigurator {...props} />
  </ObjValidator>
);
Scrivito.provideComponent("ProjectConfigurator", CheckedPage);

export default CheckedPage;

const useRedirectEffect = () => {
  const projectId = useProjectDataStore((state) => state.projectId);
  const isManualRedirect = useProjectDataStore(
    (state) => state.isManualRedirect,
  );
  useEffect(() => {
    if (!projectId && !isManualRedirect) {
      const pid = getProjectId();
      if (!pid) {
        const redirect = async () => {
          const { welcomePageObj } = await Scrivito.load(() => {
            const homepage = getHomepage();
            const welcomePage = homepage.get("welcomePage");
            return {
              welcomePageObj: welcomePage || homepage,
            };
          });
          Scrivito.navigateTo(welcomePageObj);
        };
        redirect();
      }
    }
  }, [projectId, isManualRedirect]);
};
